import {
    ChangeDetectionStrategy,
    Component,
    computed,
    inject,
    signal,
} from '@angular/core';

import { TranslocoPipe } from '@jsverse/transloco';
import { of, switchMap } from 'rxjs';

import { ModalRef } from '~/app/shared/services/modal/modal-ref';
import { AlertComponent } from '~/app/shared/components/elements/alert/alert.component';
import { ButtonComponent } from '~/app/shared/components/elements/buttons/button/button.component';
import { SpinnerComponent } from '~/app/shared/components/elements/loaders/spinner/spinner.component';
import { RequestCommercialAppointementComponent } from '~/app/domains/explanation/components/explanation-request-commercial-appointment/request-commercial-appointement.component';
import { HelpOptionSelectorComponent } from '../../components/help-option-selector/help-option-selector.component';
import { HelpContactFormComponent, HelpContactFormSubmitData } from '../../components/help-contact-form/help-contact-form.component';
import { HelpContactFormSuccessComponent } from '../../components/help-contact-form-success/help-contact-form-success.component';
import { HelpHubspotService } from '../../services/help-hubspot.service';
import { HelpOrchestratorState, HelpOrchestratorStates } from '../../enums/help-orchestrator-state.enum';
import { HelpModalData } from '../../help.facade';

@Component({
    selector: 'eb-help-orchestrator-modal',
    templateUrl: './help-orchestrator-modal.component.html',
    styleUrl: './help-orchestrator-modal.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [
    TranslocoPipe,
    AlertComponent,
    ButtonComponent,
    SpinnerComponent,
    HelpOptionSelectorComponent,
    HelpContactFormComponent,
    HelpContactFormSuccessComponent,
    RequestCommercialAppointementComponent
],
})
export class HelpOrchestratorModalComponent {
    private ref = inject<ModalRef<void, HelpModalData>>(ModalRef);

    private hubspotService = inject(HelpHubspotService);

    protected componentState = signal<HelpOrchestratorState>(HelpOrchestratorStates.IDLE);
    protected lastFormData = signal<HelpContactFormSubmitData | null>(null);

    protected readonly HelpOrchestratorStates = HelpOrchestratorStates;

    protected viewModel = computed(() => ({
        componentState: this.componentState(),
        calendlyUrl: this.ref.data.calendlyUrl,
        formContacts: this.ref.data.formContacts,
        hubspotId: this.ref.data.hubspotId,
        userData: this.ref.data.userData,
        hasFormContacts: this.ref.data.formContacts.length > 0,
        hasCalendly: !!this.ref.data.calendlyUrl,
    }));

    public setComponentState(newState: HelpOrchestratorState): void {
        this.componentState.set(newState);
    }

    public onFormSubmit(formData: HelpContactFormSubmitData): void {
        const hubspotId = this.ref.data.hubspotId;
        const userData = this.ref.data.userData;

        if (!hubspotId || !userData) {
            this.componentState.set(HelpOrchestratorStates.FAILURE);
            return;
        }

        this.lastFormData.set(formData);
        this.componentState.set(HelpOrchestratorStates.LOADING);

        const files = formData.files ?? [];
        const uploadFiles$ = files.length > 0
            ? this.hubspotService.uploadFiles(files)
            : of({ urls: [] as string[] });

        uploadFiles$.pipe(
            switchMap(uploadResponse =>
                this.hubspotService.submitForm(hubspotId, formData.formUuid, {
                    email: userData.email,
                    firstname: userData.firstname,
                    lastname: userData.lastname,
                    company: userData.company,
                    subject: formData.subject,
                    message: formData.message,
                    fileUrls: uploadResponse.urls,
                })
            )
        ).subscribe({
            next: () => {
                this.componentState.set(HelpOrchestratorStates.SUCCESS);
            },
            error: () => {
                this.componentState.set(HelpOrchestratorStates.FAILURE);
            },
        });
    }

    public retry(): void {
        const lastData = this.lastFormData();
        if (lastData) {
            this.onFormSubmit(lastData);
        } else {
            this.componentState.set(HelpOrchestratorStates.FORM);
        }
    }

    public goBack(): void {
        const currentState = this.componentState();

        switch (currentState) {
            case HelpOrchestratorStates.APPOINTMENT:
            case HelpOrchestratorStates.FORM:
                this.componentState.set(HelpOrchestratorStates.IDLE);
                break;
            default:
                break;
        }
    }

    public close(): void {
        this.ref.close();
    }
}
